import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';

import {
  Feature,
  FeatureNames,
  HolidayProgramCommands,
  HolidayProgramProperties,
  IsEnabledProperty,
  OwnershipType,
} from '@vi-iot/client';

import { GTMService, UIService } from '@services';
import { accordionAnimation, arrowRotateAnimation } from '@shared-ui';
import {
  AuthStore,
  DevicesStore,
  FeatureService,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  isReady,
  RemoteConfigStore,
  RemoteDiagnosticsStore,
} from '@store';

@Component({
  selector: 'app-sticky-panel',
  templateUrl: './sticky-panel.component.html',
  styleUrls: ['./sticky-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [arrowRotateAnimation, accordionAnimation],
})
export class StickyPanelComponent implements OnInit, OnDestroy {
  isExpanded = false;
  firstCircuitId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  circuits$: Observable<Feature<IsEnabledProperty, never>>;
  holidayProgram$: Observable<Feature<HolidayProgramProperties, HolidayProgramCommands>>;
  availableCircuits: Array<string>;
  isMultiFamily: boolean;
  isInstallationVitocrossalNAType: boolean;
  ngUnsubscribe: Subject<void> = new Subject<void>();
  isSafari: boolean;
  OwnershipType = OwnershipType;

  @Input() gatewaySerial: string;

  constructor(
    public authStore: AuthStore,
    public featureService: FeatureService,
    public installationDetailsStore: InstallationDetailsStore,
    public remoteConfigStore: RemoteConfigStore,
    public remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private gtmService: GTMService,
    private uiService: UIService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private installationFeaturesStore: InstallationFeaturesStore,
    private devicesStore: DevicesStore
  ) {}

  ngOnInit(): void {
    this.isSafari = this.uiService.isSafariBrowser();
    this.circuits$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuits
    );
    this.adjustModalPosition();

    this.circuits$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => isReady(feature)),
        filter((feature) => !!feature?.properties?.enabled?.value?.length),
        tap((feature) => {
          this.availableCircuits = feature?.properties?.enabled?.value;
        }),
        map((feature) => feature.properties.enabled.value[0])
      )
      .subscribe((circuitId) => {
        this.firstCircuitId$.next(circuitId);
      });

    this.devicesStore.isMultifamily$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((isMulti) => {
      this.isMultiFamily = isMulti;
    });

    this.uiService.closeExpandedMobileStickyPanel$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.isExpanded = false;
      });

    this.installationDetailsStore.isInstallationVitocrossalNAType$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isInstallationVitocrossalNAType) => {
        this.isInstallationVitocrossalNAType = isInstallationVitocrossalNAType;
      });

    this.holidayProgram$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitSingleFamilyHolidayProgram
    );
  }

  toggle(): void {
    this.isExpanded = !this.isExpanded;
    this.uiService.isSideNavOpenSubject$.next(this.isExpanded);
    if (this.isExpanded) {
      this.gtmService.track('onPanelExpand', 'Information panel', 'installationDetails');
    } else {
      this.gtmService.track('onPanelCollapse', 'Information panel', 'installationDetails');
    }
  }

  get expandedState(): 'expanded' | 'collapsed' {
    return this.isExpanded ? 'expanded' : 'collapsed';
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.renderer.removeClass(this.document.body, 'center-modal');
  }

  private adjustModalPosition(): void {
    this.uiService.isAboveMediumWidth$
      .pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
      .subscribe((isAboveMediumWidth) => {
        if (isAboveMediumWidth) {
          this.renderer.addClass(this.document.body, 'center-modal');
        } else {
          this.renderer.removeClass(this.document.body, 'center-modal');
        }
      });
  }
}
